// ===========================================================================
// ReHome — Csempés térkép vetítési matematikája (Web Mercator)
//
// Külön, tiszta modul, mert ez az a fajta számítás, ami elrontva NEM omlik
// össze: a térkép megjelenik, csak épp nem ott, ahol az állatot megtalálták.
// Egy rossz előjel vagy egy kihagyott radián-átváltás néhány száz kilométeres
// eltérést okoz, amit ránézésre senki nem vesz észre.
//
// A vetítés a szabványos "slippy map" séma (OpenStreetMap, Google, Bing
// egyaránt ezt használja): a Föld egy 256 * 2^zoom pixeles négyzetre képződik,
// az y tengelyen Mercator-torzítással.
// ===========================================================================

/** Egy csempe éle pixelben — a slippy map séma rögzített értéke. */
export const TILE_SIZE = 256;

/** A Mercator-vetítés a pólusokon végtelenbe futna; itt vágódik el. */
export const MAX_MERCATOR_LAT = 85.05112878;

export interface WorldPoint {
  /** Pixelkoordináta az adott nagyítás teljes világtérképén. */
  x: number;
  y: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Földrajzi koordináta -> pixelkoordináta az adott nagyítás világtérképén.
 *
 * A szélesség a Mercator-határra vágódik: a 90. fok matematikailag végtelen
 * pixelre esne, és az egész térkép használhatatlan `Infinity` értékeket kapna.
 * A hosszúság körbefordul (a 181. fok ugyanaz, mint a -179.), így egy
 * dátumvonalon átnyúló érték sem visz ki a térképről.
 */
export const latLngToWorld = (lat: number, lng: number, zoom: number): WorldPoint => {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const safeLat = clamp(lat, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT);
  const wrappedLng = ((((lng + 180) % 360) + 360) % 360) - 180;

  const x = ((wrappedLng + 180) / 360) * scale;

  const sinLat = Math.sin((safeLat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;

  return { x, y };
};

export interface TilePlacement {
  /** Csempe-azonosítók a csempeszerver URL-jéhez. */
  x: number;
  y: number;
  z: number;
  /** Bal felső sarok pozíciója a nézeten belül, pixelben (lehet negatív). */
  left: number;
  top: number;
}

export interface TileLayout {
  tiles: TilePlacement[];
  /** A középpont helye a nézeten belül — ide kerül a jelölő. */
  markerLeft: number;
  markerTop: number;
}

/**
 * Egy `width` x `height` pixeles nézetet lefedő csempék, a megadott
 * koordinátára középezve.
 *
 * A nézet szélein a csempék szándékosan túllógnak (negatív `left`/`top`):
 * a befoglaló elem vágja le őket. Enélkül a szélek üresen maradnának, ami
 * pont a leggyakoribb eset — a keresett pont ritkán esik csempehatárra.
 *
 * A függőlegesen kilógó csempék kimaradnak (a világtérkép fölött/alatt nincs
 * mit mutatni), a vízszintesen kilógók viszont körbefordulnak — a dátumvonal
 * két oldala ugyanaz a térkép.
 */
export const buildTileLayout = (
  lat: number,
  lng: number,
  zoom: number,
  width: number,
  height: number
): TileLayout => {
  const center = latLngToWorld(lat, lng, zoom);
  const tileCount = Math.pow(2, zoom);

  // A nézet bal felső sarkának világ-pixelkoordinátája.
  const originX = center.x - width / 2;
  const originY = center.y - height / 2;

  const firstTileX = Math.floor(originX / TILE_SIZE);
  const firstTileY = Math.floor(originY / TILE_SIZE);
  const lastTileX = Math.floor((originX + width) / TILE_SIZE);
  const lastTileY = Math.floor((originY + height) / TILE_SIZE);

  const tiles: TilePlacement[] = [];
  for (let ty = firstTileY; ty <= lastTileY; ty++) {
    // A világtérkép tetején/alján nincs csempe — ezeket kihagyjuk.
    if (ty < 0 || ty >= tileCount) continue;
    for (let tx = firstTileX; tx <= lastTileX; tx++) {
      tiles.push({
        // Vízszintesen körbefordul (a Föld gömb).
        x: ((tx % tileCount) + tileCount) % tileCount,
        y: ty,
        z: zoom,
        left: tx * TILE_SIZE - originX,
        top: ty * TILE_SIZE - originY,
      });
    }
  }

  return { tiles, markerLeft: width / 2, markerTop: height / 2 };
};

/**
 * Az OpenStreetMap alapértelmezett csempeszervere.
 *
 * A `subdomain` a régi a/b/c kiszolgálók helyett egyetlen host — az OSM
 * 2023 óta ezt ajánlja, a HTTP/2 miatt a szórásnak nincs haszna.
 */
export const osmTileUrl = (x: number, y: number, z: number): string =>
  `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
